import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Upload, Image, Video, FileText, Trash2, Eye, Sparkles, Filter, X, Calendar, CheckCircle, Clock, AlertCircle } from "lucide-react";
import ClientBackground from "../components/ClientBackground";
import api from "../api";
import LoadingSpinner from "../components/LoadingSpinner";

export default function MediaAnalysis() {
  const { childId } = useParams();
  const navigate = useNavigate();
  
  const [mediaList, setMediaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState("all");
  const [stats, setStats] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [uploadData, setUploadData] = useState({
    media_type: "photo",
    files: [],
    notes: ""
  });

  const fetchMediaList = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/children/media-analysis/by-child/?child_id=${childId}`;
      
      if (selectedType !== "all") {
        url = `/children/media-analysis/by-type/?child_id=${childId}&media_type=${selectedType}`;
      }
      
      const response = await api.get(url);
      setMediaList(response.data || []);
    } catch (error) {
      console.error("Error fetching media:", error);
      setMediaList([]);
    } finally {
      setLoading(false);
    }
  }, [childId, selectedType]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get(`/children/media-analysis/statistics/?child_id=${childId}`);
      setStats(response.data || {});
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, [childId]);

  useEffect(() => {
    fetchMediaList();
    fetchStats();
  }, [childId, selectedType, fetchMediaList, fetchStats]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setUploadData({ ...uploadData, files });
  };

  const handleUpload = async () => {
    if (uploadData.files.length === 0) {
      alert("Please select at least one file");
      return;
    }

    try {
      setUploading(true);
      const currentDate = new Date().toISOString().split('T')[0];
      const formData = new FormData();
      
      if (uploadData.files.length === 1) {
        const file = uploadData.files[0];
        formData.append('child', childId);
        formData.append('media_file', file);
        formData.append('media_type', uploadData.media_type);
        formData.append('media_date', currentDate);
        formData.append('title', file.name);
        formData.append('notes', uploadData.notes);
        
        await api.post('/children/media-analysis/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        uploadData.files.forEach(file => {
          formData.append('files', file);
        });
        formData.append('child_id', childId);
        formData.append('media_type', uploadData.media_type);
        formData.append('media_date', currentDate);
        
        await api.post('/children/media-analysis/bulk-upload/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      setShowUploadModal(false);
      setUploadData({
        media_type: "photo",
        files: [],
        notes: ""
      });
      
      fetchMediaList();
      fetchStats();
    } catch (error) {
      console.error("Error uploading media:", error);
      alert("Failed to upload media. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleAnalyze = async (mediaId) => {
    try {
      await api.post(`/children/media-analysis/${mediaId}/analyze/`);
      fetchMediaList();
    } catch (error) {
      console.error("Error analyzing media:", error);
      alert("Failed to analyze media. Please try again.");
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm("Are you sure you want to delete this media?")) {
      return;
    }

    try {
      await api.delete(`/children/media-analysis/${mediaId}/`);
      setMediaList(mediaList.filter(m => m.id !== mediaId));
      setSelectedMedia(null);
      fetchStats();
    } catch (error) {
      console.error("Error deleting media:", error);
      alert("Failed to delete media. Please try again.");
    }
  };

  const getMediaIcon = (type) => {
    switch (type) {
      case "photo":
        return <Image className="w-5 h-5" />;
      case "video":
        return <Video className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      completed: {
        bg: "bg-gradient-to-r from-green-500 to-emerald-500",
        icon: <CheckCircle className="w-3.5 h-3.5" />,
        text: "Completed"
      },
      processing: {
        bg: "bg-gradient-to-r from-blue-500 to-cyan-500",
        icon: <Clock className="w-3.5 h-3.5 animate-spin" />,
        text: "Processing"
      },
      failed: {
        bg: "bg-gradient-to-r from-red-500 to-rose-500",
        icon: <AlertCircle className="w-3.5 h-3.5" />,
        text: "Failed"
      },
      pending: {
        bg: "bg-gradient-to-r from-amber-500 to-orange-500",
        icon: <Clock className="w-3.5 h-3.5" />,
        text: "Pending"
      }
    };

    const config = configs[status] || configs.pending;
    return (
      <div className={`${config.bg} text-white px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 shadow-lg`}>
        {config.icon}
        {config.text}
      </div>
    );
  };

  const mediaTypes = [
    { value: "all", label: "All Media", icon: <Filter className="w-4 h-4" />, gradient: "from-purple-500 to-pink-500" },
    { value: "photo", label: "Photos", icon: <Image className="w-4 h-4" />, gradient: "from-blue-500 to-cyan-500" },
    { value: "video", label: "Videos", icon: <Video className="w-4 h-4" />, gradient: "from-rose-500 to-orange-500" },
  ];

  if (loading && mediaList.length === 0) {
    return (
      <div className="relative min-h-screen">
        <ClientBackground />
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <ClientBackground />
      <div className="relative z-10 min-h-screen pb-8">
        {/* Header */}
        <div className="bg-white/30 backdrop-blur-sm px-5 py-4 flex items-center justify-between sticky top-0 z-20">
          <button onClick={() => navigate(-1)} className="p-2">
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <h1 className="text-lg md:text-xl font-semibold text-[#1e3a5f]">
            Media Analysis
          </h1>
          <div className="w-8 h-8" /> {/* Placeholder for spacing */}
        </div>

        {/* Statistics */}
        <div className="px-5 md:px-8 lg:px-12 pt-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-6xl mx-auto">
            <div className="group bg-gradient-to-br from-white to-gray-50 rounded-3xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:-translate-y-1">
              <div className="flex items-center justify-between mb-3">
                <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl shadow-lg">
                  <FileText className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {stats.total_media || 0}
              </div>
              <div className="text-sm text-gray-600 font-medium mt-1">Total Media</div>
            </div>

            <div className="group bg-gradient-to-br from-white to-gray-50 rounded-3xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:-translate-y-1">
              <div className="flex items-center justify-between mb-3">
                <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl shadow-lg">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                {stats.analyzed || 0}
              </div>
              <div className="text-sm text-gray-600 font-medium mt-1">Analyzed</div>
            </div>

            <div className="group bg-gradient-to-br from-white to-gray-50 rounded-3xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:-translate-y-1">
              <div className="flex items-center justify-between mb-3">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl shadow-lg">
                  <Image className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                {stats.photos || 0}
              </div>
              <div className="text-sm text-gray-600 font-medium mt-1">Photos</div>
            </div>

            <div className="group bg-gradient-to-br from-white to-gray-50 rounded-3xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:-translate-y-1">
              <div className="flex items-center justify-between mb-3">
                <div className="p-3 bg-gradient-to-br from-rose-500 to-orange-500 rounded-2xl shadow-lg">
                  <Video className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="text-3xl font-bold bg-gradient-to-r from-rose-600 to-orange-600 bg-clip-text text-transparent">
                {stats.videos || 0}
              </div>
              <div className="text-sm text-gray-600 font-medium mt-1">Videos</div>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="px-5 md:px-8 lg:px-12 pt-8">
          <div className="bg-white/50 backdrop-blur-sm p-2 rounded-full flex items-center gap-2 max-w-md mx-auto shadow-inner">
            {mediaTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => setSelectedType(type.value)}
                className={`flex-1 px-4 py-2.5 rounded-full text-sm font-bold whitespace-nowrap flex items-center justify-center gap-2 transition-all duration-300 ${
                  selectedType === type.value
                    ? `bg-white text-[#1e3a5f] shadow-lg`
                    : "text-gray-500 hover:bg-white/50"
                }`}
              >
                {type.icon}
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Media Grid */}
        <div className="px-5 md:px-8 lg:px-12 pt-8 pb-8">
          <div className="max-w-6xl mx-auto">
            {mediaList.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {mediaList.map((media) => (
                  <div
                    key={media.id}
                    className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
                  >
                    {/* Media Preview */}
                    <div className="relative aspect-video bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                      {media.media_type === "photo" && media.file_url ? (
                        <img
                          src={media.file_url}
                          alt={media.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="p-6 bg-white/50 backdrop-blur-sm rounded-3xl">
                            {getMediaIcon(media.media_type)}
                          </div>
                        </div>
                      )}
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      
                      {/* Status Badge */}
                      <div className="absolute top-3 right-3">
                        {getStatusBadge(media.analysis_status)}
                      </div>
                    </div>

                    {/* Media Info */}
                    <div className="p-5">
                      <h3 className="font-bold text-gray-900 mb-2 truncate text-lg">
                        {media.title || "Untitled"}
                      </h3>
                      
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                        <Calendar className="w-4 h-4" />
                        {new Date(media.media_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedMedia(media)}
                          className="flex-1 py-2.5 px-4 bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 hover:scale-105 active:scale-95"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                        
                        {media.analysis_status === "pending" && (
                          <button
                            onClick={() => handleAnalyze(media.id)}
                            className="flex-1 py-2.5 px-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 hover:scale-105 active:scale-95"
                          >
                            <Sparkles className="w-4 h-4" />
                            Analyze
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleDelete(media.id)}
                          className="py-2.5 px-4 bg-gradient-to-r from-red-50 to-rose-50 text-red-600 rounded-xl text-sm font-semibold hover:from-red-100 hover:to-rose-100 transition-all duration-300 hover:scale-105 active:scale-95"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center shadow-inner">
                  <Image className="w-16 h-16 text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">No media yet</h3>
                <p className="text-gray-500 mb-8 max-w-md mx-auto">Start building your media library by uploading photos and videos</p>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="px-10 py-5 bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] text-white rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-[#1e3a5f]/40 transition-all duration-300 hover:scale-105 active:scale-95 inline-flex items-center gap-3"
                >
                  <Upload className="w-6 h-6" />
                  Upload First Media
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl transform transition-all animate-in slide-in-from-bottom-4 duration-300">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Upload Media</h2>
                  <p className="text-sm text-gray-500 mt-1">Add photos or videos to your collection</p>
                </div>
                <button 
                  onClick={() => setShowUploadModal(false)} 
                  className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Media Type
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setUploadData({ ...uploadData, media_type: "photo" })}
                      className={`p-4 rounded-2xl border-2 transition-all duration-300 ${
                        uploadData.media_type === "photo"
                          ? "border-blue-500 bg-blue-50 shadow-lg scale-105"
                          : "border-gray-200 hover:border-gray-300 hover:shadow"
                      }`}
                    >
                      <Image className={`w-8 h-8 mx-auto mb-2 ${uploadData.media_type === "photo" ? "text-blue-600" : "text-gray-400"}`} />
                      <div className={`text-sm font-semibold ${uploadData.media_type === "photo" ? "text-blue-600" : "text-gray-600"}`}>
                        Photo
                      </div>
                    </button>
                    <button
                      onClick={() => setUploadData({ ...uploadData, media_type: "video" })}
                      className={`p-4 rounded-2xl border-2 transition-all duration-300 ${
                        uploadData.media_type === "video"
                          ? "border-rose-500 bg-rose-50 shadow-lg scale-105"
                          : "border-gray-200 hover:border-gray-300 hover:shadow"
                      }`}
                    >
                      <Video className={`w-8 h-8 mx-auto mb-2 ${uploadData.media_type === "video" ? "text-rose-600" : "text-gray-400"}`} />
                      <div className={`text-sm font-semibold ${uploadData.media_type === "video" ? "text-rose-600" : "text-gray-600"}`}>
                        Video
                      </div>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={uploadData.notes}
                    onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
                    placeholder="Add any context for the AI analysis..."
                    className="w-full p-3 border-2 border-gray-200 rounded-xl mb-4"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Select Files
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      multiple
                      accept="image/*,video/*,.pdf"
                      onChange={handleFileSelect}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    />
                    <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center hover:border-[#1e3a5f] hover:bg-gray-50 transition-all duration-300 cursor-pointer">
                      <Upload className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p className="text-sm font-semibold text-gray-700 mb-1">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">
                        Support for images, videos, and PDFs
                      </p>
                    </div>
                  </div>
                  {uploadData.files.length > 0 && (
                    <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-200">
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-semibold">
                          {uploadData.files.length} file{uploadData.files.length > 1 ? 's' : ''} selected
                        </span>
                      </div>
                      <div className="mt-2 space-y-1">
                        {uploadData.files.map((file, index) => (
                          <div key={index} className="text-xs text-green-600 truncate">
                            • {file.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={handleUpload}
                  disabled={uploading || uploadData.files.length === 0}
                  className="w-full py-4 bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] text-white rounded-2xl font-semibold hover:shadow-xl transition-all duration-300 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed hover:scale-105 active:scale-95 disabled:scale-100 flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Upload {uploadData.files.length > 0 && `(${uploadData.files.length})`}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Media Modal */}
        {selectedMedia && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl transform transition-all animate-in slide-in-from-bottom-4 duration-300">
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {selectedMedia.title || "Media Details"}
                  </h2>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(selectedMedia.analysis_status)}
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedMedia(null)} 
                  className="p-2 hover:bg-gray-100 rounded-xl transition-colors ml-4"
                >
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>

              {/* Media Preview */}
              {selectedMedia.media_type === "photo" && selectedMedia.file_url && (
                <div className="mb-6 rounded-2xl overflow-hidden shadow-xl border border-gray-200">
                  <img
                    src={selectedMedia.file_url}
                    alt={selectedMedia.title}
                    className="w-full"
                  />
                </div>
              )}

              {/* Media Info Grid */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-100">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-blue-500 rounded-xl">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-700">Date</h3>
                  </div>
                  <p className="text-lg font-bold text-gray-900 ml-11">
                    {new Date(selectedMedia.media_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-purple-500 rounded-xl">
                      {getMediaIcon(selectedMedia.media_type)}
                    </div>
                    <h3 className="text-sm font-semibold text-gray-700">Type</h3>
                  </div>
                  <p className="text-lg font-bold text-gray-900 capitalize ml-11">
                    {selectedMedia.media_type}
                  </p>
                </div>
              </div>

              {selectedMedia.analysis_description && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200 mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-green-500 rounded-xl">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">AI Analysis</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed ml-11">
                    {selectedMedia.analysis_description}
                  </p>
                </div>
              )}

              {selectedMedia.analysis_status === "pending" && (
                <button
                  onClick={() => {
                    handleAnalyze(selectedMedia.id);
                    setSelectedMedia(null);
                  }}
                  className="w-full py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-2xl font-semibold hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 hover:scale-105 active:scale-95"
                >
                  <Sparkles className="w-5 h-5" />
                  Analyze Now with AI
                </button>
              )}
            </div>
          </div>
        )}

        {/* Floating Upload Button */}
        <button
          onClick={() => setShowUploadModal(true)}
          className="fixed bottom-8 right-8 bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] text-white p-4 rounded-full shadow-2xl hover:shadow-[#1e3a5f]/50 transition-all duration-300 hover:scale-110 active:scale-95 z-30 flex items-center gap-3 group"
        >
          <Upload className="w-6 h-6" />
          <span className="hidden group-hover:block pr-2 font-semibold">Upload</span>
        </button>
      </div>
    </div>
  );
}
